<template>
  <el-drawer
    :visible="visible"
    :show-close="true"
    :wrapper-closable="true"
    size="520px"
    append-to-body
    custom-class="complaint-drawer complaint-detail-drawer"
    @close="$emit('close')"
  >
    <template #title>
      <div class="complaint-drawer__title">
        <span class="complaint-drawer__title-text">{{ headerTitle }}</span>
        <p v-if="row" class="complaint-drawer__subtitle">{{ drawerSubtitle }}</p>
        <div v-if="row" class="complaint-drawer__title-tags">
          <el-tag size="mini" :type="statusTag">{{ statusLabel }}</el-tag>
          <el-tag v-if="overdueDays != null" size="mini" type="danger">超期 {{ overdueDays }} 天</el-tag>
          <el-tag v-if="replyOntimeTag" size="mini" :type="replyOntimeTag">是否按时：{{ row.replyOntime }}</el-tag>
          <el-tag v-else-if="!row.replyActual" size="mini" type="info">尚未判定按时</el-tag>
        </div>
      </div>
    </template>

    <div v-if="row" class="complaint-drawer__shell">
      <div class="complaint-drawer__body">
        <EntityInfoCard
          class="complaint-drawer__card complaint-drawer__card--registration"
          title="投诉登记"
          description="Excel 导入，只读"
          collapsible
          :collapsed.sync="basicCollapsed"
          :default-collapsed="true"
          :columns="2"
        >
          <EntityInfoItem label="闭环状态" :value="statusText" />
          <EntityInfoItem label="投诉日期" :value="row.date" />
          <EntityInfoItem label="部门链路" :value="deptChain" />
          <EntityInfoItem label="源表行号" :value="`第 ${row.sourceRow} 行`" />
          <EntityInfoItem label="是否重复异常" :value="row.repeat" />
          <EntityInfoItem label="投诉部门" :value="row.deptC" />
          <EntityInfoItem label="生产部门" :value="row.deptP" />
          <EntityInfoItem label="负责人" :value="row.fzr" />
          <EntityInfoItem label="机种" :value="row.jz" />
          <EntityInfoItem label="品名" :value="row.pm" />
          <EntityInfoItem label="料号" :value="row.lh" />
          <EntityInfoItem label="周" :value="row.week" />
          <EntityInfoItem label="月" :value="row.month" />
          <div class="entity-info-item--full">
            <EntityInfoItem label="异常描述" :value="row.yc" />
          </div>
        </EntityInfoCard>

        <EntityInfoCard class="complaint-drawer__card" title="原因与对策" description="只读" :columns="1">
          <div class="entity-info-item--full">
            <EntityInfoItem label="原因分析" :value="row.cause" />
          </div>
          <div class="entity-info-item--full">
            <EntityInfoItem label="临时对策" :value="row.temp" />
          </div>
          <div class="entity-info-item--full">
            <EntityInfoItem label="永久对策" :value="row.permanent" />
          </div>
        </EntityInfoCard>

        <EntityInfoCard class="complaint-drawer__card" title="异常回复" description="只读" :columns="2">
          <EntityInfoItem label="计划完成时间" :value="replyPlanDisplay" />
          <EntityInfoItem label="实际回复时间" :value="row.replyActual" />
          <EntityInfoItem label="是否按时" :value="row.replyOntime" />
        </EntityInfoCard>

        <EntityInfoCard class="complaint-drawer__card" title="不良品处理" description="只读" :columns="2">
          <EntityInfoItem label="计划完成时间" :value="row.defectPlan" />
          <EntityInfoItem label="实际完成时间" :value="row.defectActual" />
          <EntityInfoItem label="是否按时" :value="row.defectOntime" />
        </EntityInfoCard>

        <EntityInfoCard class="complaint-drawer__card complaint-drawer__card--muted" title="对策结案" description="只读" :columns="2">
          <EntityInfoItem label="预计结案时间" :value="row.closePlan" />
          <EntityInfoItem label="实际结案时间" :value="row.closeActual" />
          <EntityInfoItem label="是否按时" :value="row.closeOntime" />
        </EntityInfoCard>
      </div>
    </div>
  </el-drawer>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';

import { ComplaintRow } from '@/api/import';
import { EntityInfoCard, EntityInfoItem } from '@/components';
import {
  CLOSURE_STATUS_LABEL,
  closureLabel,
  closureStatusOf,
  overdueDays as closureOverdueDays,
  type ClosureStatus,
} from '@/domain/complaint-closure';
import { complaintDeptChain, complaintDrawerSubtitle, complaintPartTitle } from '@/domain/complaint-present';
import { SIM_TODAY } from '@/utils/time';

const TAG: Record<ClosureStatus, 'primary' | 'success' | 'warning' | 'danger' | 'info'> = {
  overdueReply: 'danger',
  awaitingReply: 'warning',
  defectPending: 'warning',
  temp: 'warning',
  verifying: 'primary',
  closed: 'success',
};

export default Vue.extend({
  name: 'ComplaintDetailDrawer',
  components: { EntityInfoCard, EntityInfoItem },
  props: {
    visible: { type: Boolean, default: false },
    row: { type: Object as PropType<ComplaintRow | null>, default: null },
    personOptions: { type: Array as PropType<string[]>, default: () => [] },
  },
  data() {
    return {
      basicCollapsed: true,
    };
  },
  computed: {
    headerTitle(): string {
      if (!this.row) return '投诉详情';
      return complaintPartTitle(this.row);
    },
    drawerSubtitle(): string {
      if (!this.row) return '';
      return complaintDrawerSubtitle(this.row);
    },
    deptChain(): string {
      if (!this.row) return '';
      return complaintDeptChain(this.row);
    },
    statusLabel(): string {
      if (!this.row) return '';
      return CLOSURE_STATUS_LABEL[closureStatusOf(this.row, SIM_TODAY)];
    },
    statusText(): string {
      if (!this.row) return '';
      return closureLabel(this.row, SIM_TODAY);
    },
    overdueDays(): number | null {
      if (!this.row) return null;
      return closureOverdueDays(this.row, SIM_TODAY);
    },
    replyOntimeTag(): 'success' | 'warning' | null {
      if (!this.row?.replyActual || !this.row.replyOntime) return null;
      return this.row.replyOntime === '是' ? 'success' : 'warning';
    },
    replyPlanDisplay(): string {
      if (!this.row?.replyPlan) return '—';
      const plan = this.row.replyPlan;
      if (this.overdueDays != null) return `${plan}（已超 ${this.overdueDays} 天）`;
      return plan;
    },
    statusTag(): 'primary' | 'success' | 'warning' | 'danger' | 'info' {
      if (!this.row) return 'info';
      return TAG[closureStatusOf(this.row, SIM_TODAY)];
    },
  },
  watch: {
    visible(open: boolean) {
      if (open) this.basicCollapsed = true;
    },
  },
});
</script>

<style lang="scss" scoped>
.complaint-drawer__title {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-right: 24px;
}

.complaint-drawer__title-text {
  font-size: 16px;
  font-weight: 600;
  color: $color-text;
  line-height: 1.35;
}

.complaint-drawer__subtitle {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: $color-text-secondary;
}

.complaint-drawer__title-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.complaint-drawer__shell {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  width: 100%;
}

.complaint-drawer__body {
  --complaint-drawer-label-width: 88px;
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 20px 20px;
}

.entity-info-item--full {
  grid-column: 1 / -1;
}

.complaint-drawer__body ::v-deep .complaint-drawer__card.entity-info-card {
  padding: 0;
  overflow: hidden;
  border: 1px solid $color-border;
  border-radius: $border-radius-base;
  background: $color-surface;
}

.complaint-drawer__body ::v-deep .complaint-drawer__card--muted.entity-info-card {
  background: #fafbfc;
}

.complaint-drawer__body ::v-deep .entity-info-card__header--section {
  margin: 0;
  padding: 12px 14px 10px;
  border-bottom: 1px solid $color-hairline;
  background: #f8fafc;
}

.complaint-drawer__body ::v-deep .entity-info-card__header--collapsed {
  padding: 12px 14px;
  background: #f8fafc;
}

.complaint-drawer__body ::v-deep .entity-info-card__title {
  font-size: 13px;
  line-height: 1.35;
}

.complaint-drawer__body ::v-deep .entity-info-card__desc {
  font-size: 11px;
}

.complaint-drawer__body ::v-deep .entity-info-card__body--offset {
  padding: 12px 14px 14px;
}

.complaint-drawer__body ::v-deep .entity-info-grid {
  gap: 12px 20px;
}

.complaint-drawer__body ::v-deep .entity-info-item {
  display: grid;
  grid-template-columns: var(--complaint-drawer-label-width) minmax(0, 1fr);
  column-gap: 8px;
  align-items: start;
  line-height: 1.45;
}

.complaint-drawer__body ::v-deep .entity-info-label {
  font-size: 12px;
  text-align: right;
  color: $color-text-secondary;
}

.complaint-drawer__body ::v-deep .entity-info-value {
  font-size: 13px;
  font-weight: 400;
  color: $color-text;
  word-break: break-word;
}
</style>

<style lang="scss">
/* 与 live-case-drawer 共用的 complaint-drawer 外壳；只读详情抽屉依赖此 flex 链才能内部滚动 */
.el-drawer.complaint-drawer {
  display: flex;
  flex-direction: column;
  max-height: 100vh;
}

.el-drawer.complaint-drawer .el-drawer__header {
  flex-shrink: 0;
  margin-bottom: 0;
  padding: 16px 20px 14px;
  border-bottom: 1px solid #edf1f6;
}

.el-drawer.complaint-drawer .el-drawer__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden;
}

.el-drawer.complaint-drawer .complaint-drawer__shell {
  flex: 1;
  min-height: 0;
}

.el-drawer.complaint-detail-drawer .el-drawer__close-btn {
  font-size: 18px;
}
</style>
